import { NextSeo } from "next-seo";
import { GetStaticPaths, GetStaticProps } from "next";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { Footer } from "~/components/Footer";
import { Navbar } from "~/components/Navbar/component";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { SOCIAL_OG_IMAGE_URL } from "~/consts";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import {
    ManagerIndividualSEO,
    getManagerPageDescription,
} from "~/modules/managers/seo/ManagerIndividualSEO";
import { queryAssetFilterMeta } from "~/modules/assets/api/router";
import {
    FilterMetadata,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import { queryManagers } from "~/modules/managers/api/router";
import { ManagerIndividualPage } from "~/modules/managers/pages/individual";
import {
    getRwaAssetCountFromManager,
    Manager,
    ManagerColumns,
} from "~/modules/managers";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { getManagerURL, Routes } from "~/routes";
import {
    NEXTJS_MISS_REVALIDATE_TIMEOUT,
    NEXTJS_REVALIDATE_TIMEOUT,
} from "~/server/consts";
import { uniqueValues } from "~/utils/functions/uniqueValues";
import { isNullOrUndefined } from "~/utils/isNullOrUndefined";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import { queryAssetManagerAggregates } from "~/modules/managers/api/queryAssetManagerAggregates";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";
import { TokenTimeseriesColumns } from "~/modules/token_timeseries/types";

// // // //

export const getStaticPaths: GetStaticPaths = async () => {
    const managerListRes = await queryManagers({
        query: {
            sort: {
                field: ManagerColumns.name,
                direction: SortDirections.asc,
            },
            pagination: {
                page: 1,
                perPage: 250,
            },
        },
    });

    const managers: Manager[] = managerListRes.results;

    // Collect all slugs
    const allSlugs: Array<string> = managers
        .filter((m) => typeof m.slug === "string")
        .map((m) => m.slug) as Array<string>;

    // Ensure uniqueness just to be safe
    const uniqueSlugs = uniqueValues(allSlugs);

    // Create paths for all managers returned by the above query
    const paths = uniqueSlugs.map((each) => {
        return {
            params: {
                managerSlug: each,
            },
        };
    });

    return {
        paths,
        fallback: "blocking",
    };
};

// // // //

// Helper function to fetch a Manager by Manager.slug
async function getManagerBySlug(managerSlug: string): Promise<Manager | null> {
    const managerResBySlugStrict = await queryManagers({
        query: {
            sort: {
                field: ManagerColumns.name,
                direction: SortDirections.asc,
            },
            pagination: {
                page: 1,
                perPage: 250,
            },
        },
    });

    // Lookup manager by case-insensitive slug property
    const manager = managerResBySlugStrict.results.find(
        (each) => each.slug?.toLowerCase() === managerSlug.toLowerCase(),
    );

    // Return Manager | null
    return isNullOrUndefined(manager) ? null : manager;
}

// // // //

export const getStaticProps: GetStaticProps<{
    manager: Manager;
    assetFilterMetadata: FilterMetadata[];
}> = async (ctx) => {
    const managerSlug = ctx.params?.managerSlug as string;

    if (!managerSlug) {
        console.error("Manager slug is missing from params");
        return {
            notFound: true,
            revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT,
        };
    }

    // Fetch manager — if the API call fails, let it throw.
    // During build: this fails the build (desired).
    // During ISR: Next.js preserves the stale cached page (safer than redirecting).
    const manager = await getManagerBySlug(managerSlug);

    // Handle manager not found
    if (manager === null) {
        console.warn(
            `Manager not found for slug "${managerSlug}". Redirecting to managers list.`,
        );
        return {
            redirect: {
                permanent: false,
                destination: Routes.managers,
            },
            revalidate: NEXTJS_MISS_REVALIDATE_TIMEOUT,
        };
    }

    // Fetch Asset FilterMetadata
    let assetFilterMetadata: FilterMetadata[] = [];
    try {
        assetFilterMetadata = await queryAssetFilterMeta({
            filter: {
                field: "manager_id",
                operator: FilterOperators.Equals,
                value: manager.id,
            },
        });
    } catch (error) {
        console.error("Error fetching asset filter metadata:", error);
        // Continue with empty filter metadata rather than failing
    }

    // Log successful manager fetch
    console.info(
        `Successfully generated props for manager "${manager.slug}" (${manager.id})`,
    );

    // Query AggregateDisplayProps + aggregate data
    const { aggregates, data: aggregateData } =
        await queryAssetManagerAggregates({
            managerIDs: [manager.id],
            rwaAssetCount: getRwaAssetCountFromManager(manager),
        });

    // Define chart controls based on aggregate data
    const chartControls = {
        hasDistributedData: aggregateData.distributedValue.value > 0,
        hasRepresentedData: aggregateData.representedValue.value > 0,
        hasStablecoinsData: aggregateData.stablecoinMarketCap.value > 0,
    };

    // Query League Table Tabs
    const leagueTableTabs = await queryLeagueTablesByDistributionType({
        name: `asset-manager--${manager.id}`,
        filter: {
            field: TokenTimeseriesColumns.managerID,
            operator: FilterOperators.Equals,
            value: manager.id,
        },
        groups: [
            LeagueTableGroupTypes.networks,
            LeagueTableGroupTypes.assetClasses,
            LeagueTableGroupTypes.platforms,
        ],
    });

    return {
        props: {
            manager,
            aggregates,
            assetFilterMetadata,
            chartControls,
            leagueTableTabs,
        },
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
};

// // // //

interface Props {
    manager: Manager;
    aggregates: Array<AggregateDisplayProps>;
    assetFilterMetadata: FilterMetadata[];
    chartControls: {
        hasDistributedData: boolean;
        hasRepresentedData: boolean;
        hasStablecoinsData: boolean;
    };
    leagueTableTabs: LeagueTableTabsByDistributionType;
}

export default function ManagerPage(props: Props) {
    const {
        manager,
        aggregates,
        assetFilterMetadata,
        chartControls,
        leagueTableTabs,
    } = props;

    const pageUrl = addBaseUrl(getManagerURL({ managerSlug: manager.slug }));
    const description = getManagerPageDescription({ manager });

    return (
        <PageTrack route={Routes.managerIndividual}>
            <NextSeo
                title={manager.name}
                description={description}
                canonical={pageUrl}
                openGraph={{
                    type: "website",
                    url: pageUrl,
                    title: manager.name,
                    site_name: "RWA.xyz",
                    description,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content: [
                            manager.name,
                            manager.legal_name,
                            "asset manager",
                            "RWA",
                            "tokenized fund manager",
                            "real world assets",
                        ]
                            .filter(Boolean)
                            .join(", "),
                    },
                ]}
            />
            <ManagerIndividualSEO manager={manager} />
            <BreadcrumbSEO
                items={[
                    {
                        name: "Asset Managers",
                        href: Routes.managers,
                    },
                    {
                        name: manager.name,
                        href: getManagerURL({ managerSlug: manager.slug }),
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => (
                    <Navbar route={Routes.managerIndividual} user={user} />
                )}
            </UserFetcher>
            <SidebarLayout pathname={Routes.managerIndividual}>
                <Breadcrumbs
                    pages={[
                        {
                            name: "Asset Managers",
                            href: Routes.managers,
                        },
                        {
                            name: manager.name,
                            icon_url: manager.icon_url,
                            current: true,
                        },
                    ]}
                />
                <PageWrapper>
                    <ManagerIndividualPage
                        manager={manager}
                        aggregates={aggregates}
                        assetFilterMetadata={assetFilterMetadata}
                        chartControls={chartControls}
                        leagueTableTabs={leagueTableTabs}
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
